import { API_METHOD, BASE_API_URL } from "@/constants";
import { fetcher } from "@/lib/fetcher.config";
import {
  CertificationProfileDetail,
  CertificationProfileUpdate,
  CertificationsParams,
  CertificationsResponse,
  CertificationsStats,
  GeneratePresignedUploadUrlResponse,
  ImportUrlDataResponse,
  ProcessingImportUrlDataResponse,
  UnRevokeProfileResponse,
} from "@/types";

export async function getBusinessOwners({
  offset,
  limit,
  profileType,
  status,
  search,
}: CertificationsParams): Promise<CertificationsResponse> {
  return fetcher(
    `${BASE_API_URL}/business-owners?offset=${offset}&limit=${limit}&profileType=${profileType}&status=${status}&search=${search}`,
    {
      method: API_METHOD.GET,
    }
  );
}

export async function getBusinessOwnerById({
  profileId,
}: {
  profileId: string;
}): Promise<CertificationProfileDetail> {
  return fetcher(`${BASE_API_URL}/business-owners/${profileId}`, {
    method: API_METHOD.GET,
  });
}

export async function deleteBusinessOwnerEvidence({ s3Key }: { s3Key: string }) {
  return fetcher(`${BASE_API_URL}/scam-evidence/${s3Key}`, {
    method: API_METHOD.DELETE,
  });
}

export async function updateBusinessOwner({
  profile_id,
  profile_type,
  business,
  personal,
  verified_domains,
  status,
  admin_notes,
  reason_for_adjustment,
}: CertificationProfileUpdate) {
  return fetcher(`${BASE_API_URL}/business-owners/${profile_id}`, {
    method: API_METHOD.PUT,
    body: JSON.stringify({
      profile_type,
      business,
      personal,
      verified_domains,
      status,
      admin_notes,
      reason_for_adjustment,
    }),
  });
}

// Get certifications stats
export async function getCertificationsStats(): Promise<CertificationsStats> {
  return fetcher(`${BASE_API_URL}/business-owners/stats/by-profile-type`, {
    method: API_METHOD.GET,
  });
}

export async function revokeBadge({
  profileId,
  domain,
  reason,
}: {
  profileId: string;
  domain: string;
  reason: string;
}) {
  return fetcher(`${BASE_API_URL}/badge-revocation`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, domain, reason }),
  });
}

export async function revokeProfile({ profileId, reason }: { profileId: string; reason: string }) {
  return fetcher(`${BASE_API_URL}/profile-revocation`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, reason }),
  });
}

export async function approveDomain({ profileId, domain }: { profileId: string; domain: string }) {
  return fetcher(`${BASE_API_URL}/domain-approve`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, domain }),
  });
}

export async function rejectDomain({
  profileId,
  domain,
  reason,
}: {
  profileId: string;
  domain: string;
  reason: string;
}) {
  return fetcher(`${BASE_API_URL}/domain-reject`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, domain, reason }),
  });
}

export async function unRevokeProfile({
  profileId,
  reason,
}: {
  profileId: string;
  reason: string;
}): Promise<UnRevokeProfileResponse> {
  return fetcher(`${BASE_API_URL}/profile-unrevocation`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, reason }),
  });
}

export async function unRevokeDomain({
  profileId,
  domain,
  reason,
}: {
  profileId: string;
  domain: string;
  reason: string;
}): Promise<UnRevokeProfileResponse> {
  return fetcher(`${BASE_API_URL}/domain-unrevocation`, {
    method: API_METHOD.POST,
    body: JSON.stringify({ profile_id: profileId, domain, reason }),
  });
}

export async function importUrlData({
  isOverwrite,
  isEnhanceByAI,
  importedS3Key,
  originalFilename,
}: {
  isOverwrite: boolean;
  isEnhanceByAI: boolean;
  importedS3Key: string;
  originalFilename: string;
}): Promise<ImportUrlDataResponse> {
  return fetcher(
    `${BASE_API_URL}/import-url/import-data?isOverwrite=${isOverwrite}&isEnhanceByAI=${isEnhanceByAI}&importedS3Key=${importedS3Key}&originalFilename=${originalFilename}`,
    { method: API_METHOD.POST }
  );
}

export async function getProcessingImportUrlData({
  fileId,
}: {
  fileId: string;
}): Promise<ProcessingImportUrlDataResponse> {
  return fetcher(`${BASE_API_URL}/import-url/processing-import/${fileId}`, {
    method: API_METHOD.GET,
  });
}

export async function generatePresignedUploadUrl({
  fileExtension,
}: {
  fileExtension: string;
}): Promise<GeneratePresignedUploadUrlResponse> {
  return fetcher(
    `${BASE_API_URL}/import-url/generate-presigned-upload-url?fileExtension=${fileExtension}`,
    { method: API_METHOD.POST }
  );
}
